import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import type {
  ISelectableRestriction,
  RestrictionSet,
} from '@/components/partials/Restrictions/RestrictionSet';
import { RestrictionSchemeType } from '@/data/models/territory';
import type { ICurrentTerritoriesChangesGroup } from '@/types/product-current-changes-types';
import { getSaveErrorMessage } from '@/utils/saveErrorMessages';

const WORLDWIDE = 'worldwide';

const normalizeTerritoryReference = (reference?: string | null) =>
  reference?.split('/').pop() ?? '';

const cloneRestrictionSet = (restrictionSet: RestrictionSet) =>
  _.cloneDeep(restrictionSet);

const isAllowOnlyScheme = (scheme?: string) =>
  scheme === 'AllowOnly' ||
  scheme === RestrictionSchemeType.AllowOnly.toString();

const isDenyOnlyScheme = (scheme?: string) =>
  scheme === 'DenyOnly' || scheme === RestrictionSchemeType.DenyOnly.toString();

export const useRestrictionsModal = (
  territoryRestrictionSet: RestrictionSet,
  toggleRestrictionsModal: any,
  updateProduct: (
    restrictions: string[],
    restrictionScheme: RestrictionSchemeType
  ) => Promise<void>,
  currentChanges?: ICurrentTerritoriesChangesGroup,
  isChangeRequest = false
) => {
  const { t } = useTranslation();

  // Modal state
  const [restrictionSet, setRestrictionSet] = useState(
    cloneRestrictionSet(territoryRestrictionSet)
  );
  const [showPopUp, setShowPopUp] = useState(true);
  const [modalRestrictionScheme, setModalRestrictionScheme] = useState(
    territoryRestrictionSet.isDenyNone
      ? WORLDWIDE
      : territoryRestrictionSet.restrictionScheme.toString()
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [errorBanner, setErrorBanner] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [showCancelChangesModal, setShowCancelChangesModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Current mode helpers
  const isWorldwide = () => modalRestrictionScheme === WORLDWIDE;

  const isAllowOnly = () =>
    modalRestrictionScheme === RestrictionSchemeType.AllowOnly.toString();

  const isDenyOnly = () =>
    modalRestrictionScheme === RestrictionSchemeType.DenyOnly.toString();

  // Template helpers
  const getButtonClass = (isActive: boolean, isDisabled: boolean) =>
    `btn btn-blue${isActive ? ' active' : ''}${isDisabled ? ' disabled' : ''}`;

  const getTerritoryIndicatorClass = (isSelected: boolean) =>
    `restriction-territory-ci restriction-territory-ci-country ${isSelected ? 'restriction-territory-ci-badge' : ''}`;

  // Change request indicator helpers
  const hasRestrictionSchemeChange = Boolean(
    currentChanges?.territoryRestrictionScheme.hasChanges
  );
  const hasSavedRestrictionChanges = Boolean(
    currentChanges?.territoryRestrictionScheme.hasChanges ||
      currentChanges?.territoryRestrictions.hasChanges
  );
  const hasKnownUnchangedSavedRestrictions = Boolean(
    currentChanges && !hasSavedRestrictionChanges
  );
  const originalTerritoryRestrictionScheme = currentChanges
    ?.territoryRestrictionScheme.value as string | undefined;
  const originalTerritoryRestrictions =
    currentChanges?.territoryRestrictions.value;
  const originalTerritoryRestrictionReferences = Array.isArray(
    originalTerritoryRestrictions
  )
    ? originalTerritoryRestrictions.map(normalizeTerritoryReference)
    : [];

  const wasOriginallyAllowed = (reference: string) => {
    const normalizedReference = normalizeTerritoryReference(reference);

    if (isAllowOnlyScheme(originalTerritoryRestrictionScheme)) {
      return originalTerritoryRestrictionReferences.includes(
        normalizedReference
      );
    }

    if (isDenyOnlyScheme(originalTerritoryRestrictionScheme)) {
      return !originalTerritoryRestrictionReferences.includes(
        normalizedReference
      );
    }

    return true;
  };

  const hasTerritoryRestrictionChange = (
    restriction: ISelectableRestriction
  ) => {
    if (!currentChanges || !hasSavedRestrictionChanges) return false;

    const savedAllowed = territoryRestrictionSet.restrictionAllowed(
      restriction.Restriction
    );
    const originalAllowed = wasOriginallyAllowed(
      restriction.Restriction.Reference
    );

    return savedAllowed !== originalAllowed;
  };

  const hasRegionRestrictionChange = (group: string) =>
    territoryRestrictionSet
      .getAllInGroup(group)
      .some((restriction) => hasTerritoryRestrictionChange(restriction));

  // Validation
  const errorMessageHandling = () => {
    const restrictionsErrors: string[] = [];
    const restrictionsWarnings: string[] = [];

    if (
      (restrictionSet.allSelected() && restrictionSet.isDenyOnly) ||
      (restrictionSet.noneSelected() && restrictionSet.isAllowOnly)
    ) {
      const noTerritoriesMessage = t(
        'product.restrictions.noClearedTerritoriesWarning'
      );
      const hasUnsavedRestrictionChanges = !_.isEqual(
        restrictionSet,
        territoryRestrictionSet
      );

      if (
        isChangeRequest &&
        hasKnownUnchangedSavedRestrictions &&
        !hasUnsavedRestrictionChanges
      ) {
        restrictionsWarnings.push(noTerritoriesMessage);
      } else {
        restrictionsErrors.push(noTerritoriesMessage);
      }

      setErrorMessages(restrictionsErrors);
      setWarningMessages(restrictionsWarnings);
      setErrorBanner(true);
    } else {
      setErrorMessages([]);
      setWarningMessages([]);
      setErrorBanner(false);
    }
  };

  // Modal actions
  const closePopUp = () => {
    toggleRestrictionsModal();
    setShowPopUp(false);
  };

  const handleSubmit = async (save: boolean) => {
    setIsSubmitting(true);
    setSaveErrorMessage(null);

    try {
      if (save) {
        const territoryRestrictions = restrictionSet
          .getSelected()
          .map((restriction) => restriction.Reference);

        await updateProduct(
          territoryRestrictions,
          restrictionSet.restrictionScheme
        );
      } else {
        setRestrictionSet(cloneRestrictionSet(territoryRestrictionSet));
      }

      closePopUp();
    } catch (error) {
      setSaveErrorMessage(getSaveErrorMessage(error, isChangeRequest));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    if (_.isEqual(restrictionSet, territoryRestrictionSet)) {
      closePopUp();
    } else {
      setShowCancelChangesModal(true);
    }
  };

  // Selection updates
  const onRegionUpdated = (group: string) => {
    const newValue = !restrictionSet.allSelected(group);
    const newRestrictions = restrictionSet.restrictions.map((restriction) => {
      if (restriction.Group === group) {
        return { ...restriction, Selected: newValue };
      }

      return restriction;
    });
    const newRestrictionSet = cloneRestrictionSet(restrictionSet);

    newRestrictionSet.restrictions = newRestrictions;
    setRestrictionSet(newRestrictionSet);
  };

  const onTerritoryUpdated = (restriction: ISelectableRestriction) => {
    const newRestrictionSet = cloneRestrictionSet(restrictionSet);
    const element = newRestrictionSet.get(restriction.Restriction.Reference);

    element.Selected = !restriction.Selected;
    setRestrictionSet(newRestrictionSet);
  };

  // Restriction scheme updates
  const worldwideClicked = () => {
    const newRestrictionSet = cloneRestrictionSet(restrictionSet);

    newRestrictionSet.setRestrictionScheme(RestrictionSchemeType.DenyOnly);
    newRestrictionSet.restrictions = restrictionSet.restrictions.map(
      (restriction) => ({ ...restriction, Selected: false })
    );
    setRestrictionSet(newRestrictionSet);
    setModalRestrictionScheme(WORLDWIDE);
  };

  const allowOnlyClicked = () => {
    if (isAllowOnly()) return;

    const newRestrictionSet = cloneRestrictionSet(restrictionSet);

    newRestrictionSet.setRestrictionScheme(RestrictionSchemeType.AllowOnly);
    if (!restrictionSet.noneSelected()) {
      newRestrictionSet.restrictions = restrictionSet.restrictions.map(
        (restriction) => ({ ...restriction, Selected: !restriction.Selected })
      );
    }
    setRestrictionSet(newRestrictionSet);
    setModalRestrictionScheme(RestrictionSchemeType.AllowOnly.toString());
  };

  const denyOnlyClicked = () => {
    if (isDenyOnly()) return;

    const newRestrictionSet = cloneRestrictionSet(restrictionSet);

    newRestrictionSet.setRestrictionScheme(RestrictionSchemeType.DenyOnly);
    if (!restrictionSet.noneSelected()) {
      newRestrictionSet.restrictions = restrictionSet.restrictions.map(
        (restriction) => ({ ...restriction, Selected: !restriction.Selected })
      );
    }
    setRestrictionSet(newRestrictionSet);
    setModalRestrictionScheme(RestrictionSchemeType.DenyOnly.toString());
  };

  // Effects
  useEffect(() => {
    errorMessageHandling();
  }, [
    restrictionSet,
    currentChanges,
    isChangeRequest,
    territoryRestrictionSet,
  ]);

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [onCancel]);

  return {
    restrictionSet,
    showPopUp,
    errorMessages,
    warningMessages,
    errorBanner,
    saveErrorMessage,
    setSaveErrorMessage,
    showCancelChangesModal,
    setShowCancelChangesModal,
    isSubmitting,
    closePopUp,
    handleSubmit,
    onCancel,
    onRegionUpdated,
    onTerritoryUpdated,
    worldwideClicked,
    allowOnlyClicked,
    denyOnlyClicked,
    isWorldwide,
    isAllowOnly,
    isDenyOnly,
    getButtonClass,
    getTerritoryIndicatorClass,
    hasRestrictionSchemeChange,
    hasRegionRestrictionChange,
    hasTerritoryRestrictionChange,
  };
};
